import React, { ReactNode, useEffect, useRef } from 'react';
import { Modal } from '../Modal';
import {
  Header,
  LeftArrow,
  ModalContent,
  ModalFooter,
  modalStyles,
  RightArrow,
  sliderStyles,
  SliderContent,
  SliderInfo,
  SliderInfoText,
  carouselStyles,
  ClickZoneLeft,
  ClickZoneRight
} from './styled';
import { GallerySlider } from '../GallerySlider';
import useScreenSize from '../../../hooks/useScreenSize';
import { usePageNumberOnScroll } from '../../Ad/ItemPhotosGallery/usePageNumberOnScroll';
import { GalleryCarousel } from '../GalleryCarousel';
import { Photo } from '../../../graphql/graphql';
import { TargetedBannerIds, AdriverCustom } from '../../../types';

interface FullScreenGalleryProps {
  isOpen: boolean;
  photos: Photo[];
  initialGalleryIndex: number;
  footer: ReactNode;
  onClose: () => void;
  renderHeader: (
    pageNumber: number,
    shouldHideSliderInfo: boolean
  ) => ReactNode;
  description: string;
  adriverCustom?: AdriverCustom;
  renderDesktopCarousel?: boolean;
  withoutSafeTopPadding?: boolean;
}

export const FullScreenGallery: React.FC<FullScreenGalleryProps> = ({
  isOpen,
  photos,
  initialGalleryIndex,
  footer,
  onClose,
  renderHeader,
  description,
  adriverCustom,
  renderDesktopCarousel = true,
  withoutSafeTopPadding = false
}) => {
  const { isSmallOrLargeDesktop, isLarge } = useScreenSize();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    slidesToRender,
    pageNumber,
    scrollToNextPhoto,
    scrollToPrevPhoto,
    scrollToPhoto,
    isBannerPosition,
    isBannerActivated,
    shouldRenderBanner,
    setShouldRenderBanner,
    isNextBanner
  } = usePageNumberOnScroll(photos, scrollRef, initialGalleryIndex, isLarge);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        scrollToNextPhoto();
      }
      if (e.key === 'ArrowLeft') {
        scrollToPrevPhoto();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, scrollToNextPhoto, scrollToPrevPhoto]);

  const shouldRenderArrows = isSmallOrLargeDesktop && photos.length > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalId="current-item-gallery"
      modalStyles={modalStyles}
      cypress="current-item-gallery"
      withAnimation={false}
    >
      <ModalContent>
        <Header $withoutSafeTopPadding={withoutSafeTopPadding}>
          {renderHeader(pageNumber, isBannerPosition)}
        </Header>
        <SliderContent>
          {shouldRenderArrows && !isBannerPosition && (
            <>
              <ClickZoneLeft onClick={scrollToPrevPhoto} />
              <ClickZoneRight onClick={scrollToNextPhoto} />
            </>
          )}
          <GallerySlider
            isFullScreen
            photos={slidesToRender}
            description={description}
            scrollRef={scrollRef}
            scrollToPrevPhoto={scrollToPrevPhoto}
            scrollToNextPhoto={scrollToNextPhoto}
            sliderStyles={sliderStyles}
            shouldRenderBlurredBackground={false}
            isBannerActivated={isBannerActivated}
            isNextBanner={isNextBanner}
            setShouldRenderBanner={setShouldRenderBanner}
            shouldRenderBanner={shouldRenderBanner}
            bannerId={TargetedBannerIds.fullscreenGallery}
            isBannerPosition={isBannerPosition}
            adriverCustom={adriverCustom}
            sliderInfo={
              <SliderInfo>
                <SliderInfoText tag="body_2">
                  {pageNumber}/{photos.length}
                </SliderInfoText>
              </SliderInfo>
            }
          />
          {shouldRenderArrows && (
            <>
              <LeftArrow onClick={scrollToPrevPhoto} />
              <RightArrow onClick={scrollToNextPhoto} />
            </>
          )}
        </SliderContent>
        <ModalFooter $isCentered={photos?.length < 16}>
          {isSmallOrLargeDesktop && renderDesktopCarousel && (
            <GalleryCarousel
              photos={photos}
              pageNumber={pageNumber}
              shouldOverlayRender={false}
              scrollToPhoto={scrollToPhoto}
              carouselStyles={carouselStyles}
              carouselPhotoWidth={70}
              description={description}
            />
          )}
          {!isSmallOrLargeDesktop && footer}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
